/**
 * <panchang-card> — The five limbs for a date and place with their end times,
 * sunrise/sunset, and the day's inauspicious windows.
 *
 * Accepts a computed `panchang` object (from `panchang()`) via the `.value`
 * property. Renders nothing useful until one is set — the page owns the
 * inputs and the computation.
 *
 * © Manish Jagdish Thatte
 */

import { formatJdLocal, formatLatitude, formatLongitude } from '../utils/format.js';

const VARA_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export class PanchangCard extends HTMLElement {
  connectedCallback() {
    if (this._built) return;
    this._built = true;
    // ⚠ Keep a value set before the element was attached (print-sheet builds the card first).
    this._panch = this._panch ?? null;
    this.render();
  }

  /** @param {object | null} p */
  set value(p) {
    this._panch = p;
    this.render();
  }
  get value() { return this._panch; }

  render() {
    this.textContent = '';
    const p = this._panch;
    if (!p) {
      const note = document.createElement('p');
      note.className = 'field-note';
      note.textContent = 'Choose a date and place to compute the pañcāṅga.';
      this.append(note);
      return;
    }

    const off = p.offsetMinutes ?? 0;
    const fmt = (jd) => (jd == null ? '—' : formatJdLocal(jd, off, { seconds: true }));

    const root = document.createElement('div');
    root.className = 'panchang-card panel';

    // Place / polar banner.
    const meta = document.createElement('p');
    meta.className = 'field-note';
    meta.textContent = `${formatLatitude(p.lat)}, ${formatLongitude(p.lonEast)}`
      + (p.ayanamsa ? ` · ${p.ayanamsa} ayanāṁśa` : '');
    root.append(meta);

    if (p.polar) {
      root.append(notice(p.polarReason ?? `Polar ${p.polar}: sunrise-based windows are undefined.`, 'warning'));
    }

    // Five limbs.
    const limbs = document.createElement('dl');
    limbs.className = 'panchang-card__limbs';
    addLimb(limbs, 'Tithi',
      `${cap(p.tithi.paksha)} ${cap(p.tithi.name)}`,
      `ends ${fmt(p.tithi.endJdUT)}`);
    addLimb(limbs, 'Vāra',
      `${VARA_EN[p.vara.index]} (${p.vara.lord})`,
      p.vara.fromSunrise ? `from sunrise ${fmt(p.vara.sunriseJdUT)}` : 'civil weekday (no sunrise)');
    addLimb(limbs, 'Nakṣatra',
      `${p.nakshatra.name?.en ?? p.nakshatra.id} (pada ${p.nakshatra.pada})`,
      `ends ${fmt(p.nakshatra.endJdUT)}`);
    addLimb(limbs, 'Yoga',
      cap(p.yoga.id),
      `ends ${fmt(p.yoga.endJdUT)}`);
    addLimb(limbs, 'Karaṇa',
      cap(p.karana.name),
      `ends ${fmt(p.karana.endJdUT)}`);
    root.append(limbs);

    // Month.
    if (p.month && !p.month.unavailable) {
      const month = document.createElement('p');
      month.innerHTML = '';
      month.className = 'panchang-card__month';
      month.textContent =
        `Amānta: ${cap(p.month.amanta.label)} · Pūrṇimānta: ${cap(p.month.purnimanta.label)}`
        + (p.month.amanta.adhika ? ' · adhika-māsa' : '');
      root.append(month);
    }

    // Day lights.
    const lights = document.createElement('dl');
    lights.className = 'panchang-card__limbs';
    addLimb(lights, 'Sunrise', fmt(p.sunrise?.jdUT), '');
    addLimb(lights, 'Sunset', fmt(p.sunset?.jdUT), '');
    addLimb(lights, 'Moonrise', fmt(p.moonrise?.jdUT), '');
    addLimb(lights, 'Moonset', fmt(p.moonset?.jdUT), '');
    root.append(lights);

    // Inauspicious windows.
    const wins = document.createElement('div');
    wins.className = 'panchang-card__windows';
    const h = document.createElement('h3');
    h.textContent = 'Day windows';
    wins.append(h);
    for (const key of ['rahuKala', 'yamganda', 'gulikaKala']) {
      const w = p[key];
      const row = document.createElement('p');
      if (!w || w.unavailable) {
        row.className = 'field-note';
        row.textContent = `${labelWindow(key)}: ${w?.reason ?? 'unavailable'}`;
      } else {
        row.textContent = `${labelWindow(key)} (8th #${w.eighth}): ${fmt(w.startJdUT)} – ${fmt(w.endJdUT)}`;
      }
      wins.append(row);
    }
    if (p.currentHora) {
      const row = document.createElement('p');
      row.textContent = `Current horā: ${p.currentHora.lord} (#${p.currentHora.number})`;
      wins.append(row);
    } else if (p.horas?.unavailable) {
      const row = document.createElement('p');
      row.className = 'field-note';
      row.textContent = `Horās: ${p.horas.reason}`;
      wins.append(row);
    }
    if (p.currentChoghadiya) {
      const row = document.createElement('p');
      row.textContent =
        `Current Choghaḍiyā: ${cap(p.currentChoghadiya.name)} (${p.currentChoghadiya.quality}, ${p.currentChoghadiya.period})`;
      wins.append(row);
    } else if (p.choghadiya?.unavailable) {
      const row = document.createElement('p');
      row.className = 'field-note';
      row.textContent = `Choghaḍiyā: ${p.choghadiya.reason}`;
      wins.append(row);
    }
    root.append(wins);

    this.append(root);
  }
}

function addLimb(dl, dt, dd, note) {
  const t = document.createElement('dt');
  t.textContent = dt;
  const d = document.createElement('dd');
  d.textContent = dd;
  if (note) {
    const n = document.createElement('span');
    n.className = 'field-note';
    n.textContent = ` — ${note}`;
    d.append(n);
  }
  dl.append(t, d);
}

function labelWindow(key) {
  return { rahuKala: 'Rāhu Kāla', yamganda: 'Yamagaṇḍa', gulikaKala: 'Gulika Kāla' }[key] ?? key;
}

function cap(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function notice(text, kind = 'info') {
  const el = document.createElement('div');
  el.className = `notice notice--${kind}`;
  el.textContent = text;
  return el;
}

customElements.define('panchang-card', PanchangCard);
